// Firmas del trabajador (M45): lógica pura, sin base de datos.
//  - Forma canónica del contenido (claves ordenadas) y su SHA-256: el mismo contenido da siempre el mismo hash.
//  - Contenido congelado de cada registro (asistencia, entrega de EPP, aptitud médica) y lo que le falta para
//    prepararlo (contenido mínimo del Anexo 1) y para firmarlo (las mismas reglas que el trigger de M45).
//  - Bloqueo: con un registro en firma o firmado el origen no se edita; con firmas solo el administrador lo reabre,
//    con motivo (como reabrir_inspeccion de M41).

import { sha256Hex } from '../psicosocial-codigos';
import { conLista } from './firmas-tipos';
import type {
  AsistenteContenido, ClaseCapacitacion, Contenido, ContenidoAptitud, ContenidoAsistencia, ContenidoEpp, EstadoRegistro,
  FilaFirma, FilaRegistro, MetodoRegistro, ResultadoEvaluacion, RolFirmante, TipoRegistro,
} from './firmas-tipos';

// ── Forma canónica y hash ──

/** JSON con las claves ordenadas en todos los niveles (sin undefined): independiente del orden en que se armó. */
export function canonico(v: unknown): string {
  if (v === null || v === undefined) return 'null';
  if (Array.isArray(v)) return `[${v.map(x => canonico(x === undefined ? null : x)).join(',')}]`;
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o).filter(k => o[k] !== undefined).sort().map(k => `${JSON.stringify(k)}:${canonico(o[k])}`).join(',')}}`;
  }
  if (typeof v === 'number' && !Number.isFinite(v)) return 'null';
  return JSON.stringify(v);
}

export const hashContenido = (c: Contenido): Promise<string> => sha256Hex(canonico(c));

export interface Firmante { rol: RolFirmante; worker_id: string | null; nombre: string; cedula: string | null }

/** Lo que firma cada persona: el hash del contenido del registro más sus datos. */
export const textoFirma = (hashRegistro: string, f: Firmante) =>
  canonico({ contenido: hashRegistro, firmante: { rol: f.rol, worker_id: f.worker_id, nombre: f.nombre.trim(), cedula: f.cedula?.trim() || null } });

export const hashFirma = (hashRegistro: string, f: Firmante): Promise<string> => sha256Hex(textoFirma(hashRegistro, f));

// ── Validaciones ──

export const cedulaValida = (c: string | null | undefined) => /^\d{10}$/.test((c ?? '').trim());
export const MOTIVO_MINIMO = 10;
export function validarMotivo(m: string): string | null {
  return m.trim().length < MOTIVO_MINIMO ? `Escribe el motivo para reabrir el registro (al menos ${MOTIVO_MINIMO} caracteres).` : null;
}

// ── Contenido congelado ──

export interface CapacitacionOrigen {
  id: string; title: string; topic: string | null; trainer_name: string | null; scheduled_date: string; duration_hours: number | null;
  status: string; record_kind?: ClaseCapacitacion | null; material?: string | null; evaluation_method?: string | null;
}
export interface AsistenteOrigen {
  worker_id: string; nombre: string; cedula: string | null; evaluation_result: ResultadoEvaluacion | null; evaluation_score: number | null;
}

export function contenidoAsistencia(t: CapacitacionOrigen, asistentes: AsistenteOrigen[], empresa: string): ContenidoAsistencia {
  return {
    tipo: 'asistencia', capacitacion_id: t.id, empresa, clase: t.record_kind ?? 'capacitacion', fecha: t.scheduled_date,
    titulo: t.title.trim(), tema: (t.topic ?? '').trim(), tecnico: (t.trainer_name ?? '').trim(), material: (t.material ?? '').trim(),
    evaluacion: (t.evaluation_method ?? '').trim(), duracion_horas: t.duration_hours ?? null,
    asistentes: [...asistentes]
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es') || a.worker_id.localeCompare(b.worker_id))
      .map((a): AsistenteContenido => ({ worker_id: a.worker_id, nombre: a.nombre.trim(), cedula: (a.cedula ?? '').trim(),
        evaluacion: a.evaluation_result ?? null, nota: a.evaluation_score ?? null })),
  };
}

export interface AsignacionOrigen {
  id: string; epp: string; categoria: string | null; marca: string | null; modelo: string | null; condicion: string | null;
  assigned_date: string; expiry_date: string | null;
}
export function contenidoEpp(trabajador: { id: string; full_name: string; cedula: string | null }, fecha: string,
  items: AsignacionOrigen[], empresa: string): ContenidoEpp {
  return {
    tipo: 'epp_entrega', worker_id: trabajador.id, empresa, trabajador: trabajador.full_name.trim(), cedula: (trabajador.cedula ?? '').trim(),
    fecha, items: [...items].sort((a, b) => a.epp.localeCompare(b.epp, 'es') || a.id.localeCompare(b.id)).map(i => ({
      asignacion_id: i.id, epp: i.epp, categoria: i.categoria, marca: i.marca, modelo: i.modelo, condicion: i.condicion, vence: i.expiry_date,
    })),
  };
}

export interface ExamenOrigen { id: string; worker_id: string; exam_date: string; exam_type: string | null; aptitude: string | null; physician: string | null }
export function contenidoAptitud(e: ExamenOrigen, trabajador: { full_name: string; cedula: string | null }, empresa: string): ContenidoAptitud {
  return {
    tipo: 'aptitud_medica', examen_id: e.id, worker_id: e.worker_id, empresa, trabajador: trabajador.full_name.trim(),
    cedula: (trabajador.cedula ?? '').trim(), fecha: e.exam_date, tipo_examen: e.exam_type ?? '', aptitud: e.aptitude ?? '',
    medico: (e.physician ?? '').trim(),
  };
}

/** Contenido mínimo del Anexo 1 que falta para preparar el registro (vacío = se puede preparar). */
export function faltantesContenido(c: Contenido, extra: { realizada?: boolean } = {}): string[] {
  const f: string[] = [];
  if (c.tipo === 'asistencia') {
    if (extra.realizada === false) f.push('Marca la capacitación como Realizada.');
    if (!c.fecha) f.push('Falta la fecha.');
    if (!c.tema && !c.titulo) f.push('Falta el tema.');
    if (!c.tecnico) f.push('Falta el técnico o capacitador.');
    if (!c.material) f.push('Falta el material entregado o usado.');
    if (!c.evaluacion) f.push('Falta la forma de evaluación.');
    if (c.asistentes.length === 0) f.push('La lista de asistencia está vacía.');
    const sinCedula = c.asistentes.filter(a => !cedulaValida(a.cedula));
    if (sinCedula.length) f.push(`Sin cédula de 10 dígitos en Trabajadores: ${sinCedula.map(a => a.nombre).join(', ')}.`);
    const sinEvaluar = c.asistentes.filter(a => !a.evaluacion);
    if (sinEvaluar.length) f.push(`Falta el resultado de la evaluación de: ${sinEvaluar.map(a => a.nombre).join(', ')}.`);
  } else if (c.tipo === 'socializacion') {
    // M49 (GT 37): la evaluación es opcional; el resto como la asistencia.
    if (!c.fecha) f.push('Falta la fecha.');
    if (!c.tecnico) f.push('Falta quien socializa el procedimiento.');
    if (c.asistentes.length === 0) f.push('La lista de asistentes está vacía.');
    const sinCedula = c.asistentes.filter(a => !cedulaValida(a.cedula));
    if (sinCedula.length) f.push(`Sin cédula de 10 dígitos en Trabajadores: ${sinCedula.map(a => a.nombre).join(', ')}.`);
  } else if (c.tipo === 'epp_entrega') {
    if (!cedulaValida(c.cedula)) f.push(`${c.trabajador} no tiene una cédula de 10 dígitos en Trabajadores.`);
    if (!c.fecha) f.push('Falta la fecha de entrega.');
    if (c.items.length === 0) f.push('No hay EPP en esta entrega.');
  } else {
    if (!cedulaValida(c.cedula)) f.push(`${c.trabajador} no tiene una cédula de 10 dígitos en Trabajadores.`);
    if (!c.fecha) f.push('Falta la fecha del examen.');
    if (!c.tipo_examen) f.push('Falta el tipo de examen (ingreso o periódico).');
    if (!c.aptitud || c.aptitud === 'pendiente') f.push('Falta el resultado de aptitud.');
    if (!c.medico) f.push('Falta el nombre del médico.');
  }
  return f;
}

// ── Firmas requeridas y lo que falta para firmar ──

export interface FirmaRequerida { rol: RolFirmante; worker_id: string | null; nombre: string; obligatoria: boolean; cedula?: string | null }

export function firmasRequeridas(c: Contenido): FirmaRequerida[] {
  if (conLista(c)) {
    return [...c.asistentes.map(a => ({ rol: 'trabajador' as const, worker_id: a.worker_id, nombre: a.nombre, obligatoria: true, cedula: a.cedula })),
      { rol: 'tecnico', worker_id: null, nombre: c.tecnico, obligatoria: true }];
  }
  const trabajador: FirmaRequerida = { rol: 'trabajador', worker_id: c.worker_id, nombre: c.trabajador, obligatoria: true, cedula: c.cedula };
  return c.tipo === 'epp_entrega' ? [trabajador, { rol: 'responsable', worker_id: null, nombre: '', obligatoria: false }] : [trabajador];
}

export const claveFirma = (rol: RolFirmante, workerId: string | null) => `${rol}:${workerId ?? ''}`;

type FirmaMin = Pick<FilaFirma, 'role' | 'worker_id'> & { signer_cedula?: string | null };

/**
 * La firma de una persona requerida. Si el trabajador se borró, la firma conserva su cédula (signer_cedula) pero ya
 * no su vínculo (worker_id NULL): se empareja por la cédula del contenido congelado.
 */
export function firmaDe<F extends FirmaMin>(firmas: F[], r: Pick<FirmaRequerida, 'rol' | 'worker_id' | 'cedula'>): F | undefined {
  return firmas.find(f => f.role === r.rol && (!r.worker_id || f.worker_id === r.worker_id
    || (f.worker_id === null && !!r.cedula && f.signer_cedula === r.cedula)));
}

/** ¿La firma es de un trabajador que ya no existe en Trabajadores? */
export const firmaSinTrabajador = (f: Pick<FilaFirma, 'role' | 'worker_id'>) => f.role === 'trabajador' && f.worker_id === null;

/** ¿El registro perdió su trabajador (entrega de EPP o aptitud médica)? */
export const registroSinTrabajador = (r: Pick<FilaRegistro, 'kind' | 'worker_id'> | null | undefined) =>
  !!r && r.kind !== 'asistencia' && r.kind !== 'socializacion' && r.worker_id === null;

/** ¿El registro perdió su origen (capacitación, trabajador o examen)? */
export const registroSinOrigen = (r: Pick<FilaRegistro, 'kind' | 'worker_id' | 'training_id' | 'medical_exam_id'>) =>
  r.kind === 'asistencia' ? r.training_id === null : r.kind === 'socializacion' ? false
    : r.worker_id === null || (r.kind === 'aptitud_medica' && r.medical_exam_id === null);

/** Lo que falta para marcar el registro como firmado (las mismas reglas que el trigger de M45). */
export function faltantesParaFirmar(r: Pick<FilaRegistro, 'kind' | 'status' | 'snapshot' | 'scan_path' | 'doctor_cert_path'>,
  firmas: FirmaMin[], metodo: MetodoRegistro): string[] {
  const f: string[] = [];
  if (r.status !== 'en_firma' || !r.snapshot) return ['Prepara el registro para firma.'];
  if (metodo === 'papel') {
    if (!r.scan_path) f.push('Sube el escaneo del formato firmado.');
    return f;
  }
  const req = firmasRequeridas(r.snapshot).filter(x => x.obligatoria);
  const trab = req.filter(x => x.rol === 'trabajador');
  const firmaron = trab.filter(x => firmaDe(firmas, x)).length;
  if (r.kind === 'asistencia' || r.kind === 'socializacion') {
    if (firmaron < trab.length) f.push(`Faltan firmas de asistentes: ${firmaron} de ${trab.length}.`);
    if (!firmas.some(x => x.role === 'tecnico')) f.push('Falta la firma del técnico o capacitador.');
  } else {
    if (firmaron < 1) f.push('Falta la firma del trabajador.');
    if (r.kind === 'aptitud_medica' && !r.doctor_cert_path) f.push('Sube el certificado firmado por el médico.');
  }
  return f;
}

// ── Bloqueo ──

/** ¿El origen (capacitación y lista, examen, asignaciones de EPP) se puede editar? Solo sin registro o con uno abierto. */
export const origenEditable = (r: Pick<FilaRegistro, 'status'> | null | undefined) => !r || r.status === 'abierto';

/** ¿Hace falta reabrir (admin, con motivo) para cambiar algo? Firmado o con alguna firma. */
export const requiereReapertura = (r: Pick<FilaRegistro, 'status'> | null | undefined, nFirmas: number) =>
  !!r && (r.status === 'firmado' || (r.status === 'en_firma' && nFirmas > 0));

/** ¿Se puede volver a editar sin reabrir? En firma y sin ninguna firma. */
export const puedeVolverAEditar = (r: Pick<FilaRegistro, 'status'> | null | undefined, nFirmas: number) =>
  !!r && r.status === 'en_firma' && nFirmas === 0;

/** ¿Se puede borrar el registro? Sin ninguna firma y sin estar firmado (un firmado en papel no tiene firmas en
 *  pantalla y la base no lo deja borrar): la misma regla que el trigger de M45. */
export const puedeBorrarRegistro = (r: Pick<FilaRegistro, 'status'> | null | undefined, nFirmas: number) =>
  !!r && r.status !== 'firmado' && nFirmas === 0;

/** Estado para mostrar: sin preparar, en firma (n de m), firmado. */
export function estadoVisible(r: Pick<FilaRegistro, 'status' | 'snapshot'> | null | undefined,
  firmas: FirmaMin[]): { estado: EstadoRegistro | 'sin_registro'; texto: string } {
  if (!r) return { estado: 'sin_registro', texto: 'Sin firmar' };
  if (r.status === 'firmado') return { estado: 'firmado', texto: 'Firmado' };
  if (r.status === 'abierto' || !r.snapshot) return { estado: 'abierto', texto: 'Sin preparar' };
  const req = firmasRequeridas(r.snapshot).filter(x => x.obligatoria);
  const hechas = req.filter(x => firmaDe(firmas, x)).length;
  return { estado: 'en_firma', texto: `En firma (${hechas} de ${req.length})` };
}

export const tipoDeContenido = (c: Contenido): TipoRegistro => c.tipo;

/** Mensaje en español para los errores de la base (triggers de M45 y RLS). */
export function mensajeErrorFirmas(e: { message?: string; code?: string } | null | undefined, accion: string): string {
  const m = e?.message ?? '';
  if (e?.code === '42501' || /row-level security|permission denied/i.test(m)) return `No tienes permiso para ${accion}.`;
  if (/relation .* does not exist|schema cache|PGRST20[45]/i.test(m)) return 'Esta función todavía no está disponible para tu empresa.';
  if (/[áéíóúñ]/i.test(m) || /^(El|La|Falta|Faltan|Una|Un|Este|Escribe|Elige|Solo|Firma)\b/.test(m)) return m.replace(/\.?$/, '.');
  return `No se pudo ${accion}. Intenta de nuevo.`;
}
